import { keys } from "@/lib/i18n/portal";
import type {
  StoreSubscriptionAssignmentView,
  StoreSubscriptionPaymentView,
  SubscriptionPlanView,
} from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { StoreSubscriptionModal } from "./StoreSubscriptionModal";
import { StorePaymentModal } from "./StorePaymentModal";
import { SubscriptionPaymentProofModal } from "./SubscriptionPaymentProofModal";
import { SubscriptionPaymentDeleteButton } from "./SubscriptionPaymentDeleteButton";
import { SubscriptionPaymentRestoreButton } from "./SubscriptionPaymentRestoreButton";
import { ButtonLink } from "@/shared/components/ButtonLink";

type Translator = (key: string) => string;

type StoreSubscriptionsTabProps = {
  locale: Locale;
  storeId: string;
  assignments: StoreSubscriptionAssignmentView[];
  payments: StoreSubscriptionPaymentView[];
  plans: SubscriptionPlanView[];
  showArchived: boolean;
  isPaymentsToggleVisible: boolean;
  t: Translator;
};

export function StoreSubscriptionsTab({
  locale,
  storeId,
  assignments,
  payments,
  plans,
  showArchived,
  isPaymentsToggleVisible,
  t,
}: StoreSubscriptionsTabProps) {
  return (
    <div data-tab="subscriptions">
      <div className="grid gap-4">
        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm sm:py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-semibold text-[color:var(--text-secondary)]">
              {t(keys.admin.subscriptionAssignmentTitle)}
            </div>
            <StoreSubscriptionModal
              storeId={storeId}
              plans={plans}
              locale={locale}
            />
          </div>
          {assignments.length === 0 ? (
            <div className="mt-4 text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.subscriptionAssignmentEmpty)}
            </div>
          ) : (
            <div className="mt-4 grid gap-2">
              {assignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="rounded-lg border border-[color:var(--border-light)] px-3 py-2 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
                    <span className="min-w-0 break-words font-semibold text-[color:var(--text-primary)]">
                      {pickLocalized(
                        locale,
                        assignment.planNameAr,
                        assignment.planNameEn
                      )}
                    </span>
                    <span>•</span>
                    <span>
                      {t(keys.admin.subscriptionAssignmentStartDate)}:{" "}
                      {new Date(assignment.startDate).toLocaleDateString(locale)}
                    </span>
                    <span>•</span>
                    <span>
                      {t(keys.admin.subscriptionAssignmentChangedBy)}:{" "}
                      {assignment.createdByAdmin.name}
                    </span>
                  </div>
                  <div className="mt-2 text-[13px] text-[color:var(--text-muted)]">
                    {t(keys.admin.subscriptionAssignmentReason)}:{" "}
                    {assignment.reason}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm sm:py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-semibold text-[color:var(--text-secondary)]">
              {t(keys.admin.subscriptionPaymentsTitle)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isPaymentsToggleVisible ? (
                <ButtonLink
                  to={`/${locale}/admin/stores/${storeId}${
                    showArchived ? "" : "?showArchived=true"
                  }`}
                  variant="outline"
                  size="sm"
                  shape="rounded"
                  className="min-w-[120px] text-center text-[color:var(--text-muted)]"
                >
                  {showArchived
                    ? t(keys.admin.subscriptionPaymentHideArchived)
                    : t(keys.admin.subscriptionPaymentShowArchived)}
                </ButtonLink>
              ) : null}
              <StorePaymentModal storeId={storeId} />
            </div>
          </div>
          {payments.length === 0 ? (
            <div className="mt-4 text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.subscriptionPaymentsEmpty)}
            </div>
          ) : (
            <div className="mt-4 grid gap-2">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="rounded-lg border border-[color:var(--border-light)] px-3 py-2 text-xs"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
                      <span>
                        {t(keys.admin.subscriptionPaymentPaidAt)}:{" "}
                        {new Date(payment.paidAt).toLocaleDateString(locale)}
                      </span>
                      <span>•</span>
                      <span>
                        {t(keys.admin.subscriptionPaymentNextAt)}:{" "}
                        {payment.scheduleDueDate
                          ? new Date(payment.scheduleDueDate).toLocaleDateString(locale)
                          : "-"}
                      </span>
                      <span>•</span>
                      <span>
                        {t(keys.admin.subscriptionPaymentSettledBy)}:{" "}
                        {payment.settledByAdmin.name}
                      </span>
                      {payment.deletedAt ? (
                        <span className="rounded-full bg-[color:var(--error-100)] px-2 py-0.5 text-[13px] font-semibold text-[color:var(--error-600)]">
                          {t(keys.admin.subscriptionPaymentArchivedLabel)}
                        </span>
                      ) : null}
                    </div>
                    {payment.deletedAt ? (
                      isPaymentsToggleVisible ? (
                        <SubscriptionPaymentRestoreButton
                          storeId={storeId}
                          paymentId={payment.id}
                        />
                      ) : null
                    ) : (
                      <SubscriptionPaymentDeleteButton
                        storeId={storeId}
                        paymentId={payment.id}
                      />
                    )}
                  </div>
                  <div className="mt-2">
                    {payment.proofObjectKey ? (
                      <SubscriptionPaymentProofModal
                        storeId={storeId}
                        paymentId={payment.id}
                      />
                    ) : (
                      <div className="text-[13px] text-[color:var(--text-muted)]">
                        {t(keys.admin.subscriptionPaymentProofMissing)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
